import { wait } from '@yp/common/async.ts';
import { CustomError } from '@yp/common/core.ts';
import { ContentType, Header } from '@yp/common/http.ts';
import { isAbsent } from '@yp/common/identity.ts';
import { normalizePhoneNumber, validatePhoneNumber } from '@yp/common/identity/phone.ts';
import { Seconds } from '@yp/common/units.ts';
import { confirm } from '@yp/web.app/gui/prompts.svelte.ts';
import { DEV } from 'esm-env';
import {
  signInWithPhoneNumber,
  type ApplicationVerifier,
  type Auth,
  type ConfirmationResult,
  type User,
} from 'firebase/auth';
import {
  FireContext,
  type OnTokenChangedResult
} from '../context.ts';

type ServerSignInResult = {
  token?: string | undefined;
  status: 'success' | 'error' | 'not-found' | 'not-authorized';
};

const active = true as boolean;

let serverLookup: (phone: string) => Promise<boolean>;

if (DEV) {
  serverLookup = async (phoneNumber: string) => {
    const params = new URLSearchParams({ phone: phoneNumber });
    const res = await fetch(`/api/account?${String(params)}`);
    return res.ok;
  };
}

export async function serverSignIn(
  phoneNumber: string,
  // options?: { importOnly: boolean },
): Promise<ServerSignInResult> {
  if (!active) {
    return { token: undefined, status: 'success' };
  }
  phoneNumber = normalizePhoneNumber(phoneNumber, 'e164');
  // const importOnly = options?.importOnly ?? false;
  const res = await fetch('/api/account', {
    method: 'POST',
    headers: {
      [Header.contentType]: ContentType.text.json.type,
      // 'CSRF-Token': csrfToken  // HANDLED by sveltekit automatically
    },
    body: JSON.stringify({
      phoneNumber,
      //  importOnly
    }),
  });
  if (!res.ok) {
    return {
      status: res.status === 404 ? 'not-found' : res.status === 401 ? 'not-authorized' : 'error',
    };
  }
  // if (importOnly) {
  return { status: 'success' };
  // }
  // const json = (await res.json()) as Json;
  // const token = isObject(json) ? validate(json.loginToken, isString) : undefined;
  // return { token, status: isPresent(token) ? 'success' : 'error' };
}

async function serverUpdate(
  idToken: string,
  forceValidate = false,
): Promise<'success' | 'fail' | 'retry'> {
  const res = await fetch('/api/signin', {
    method: 'PUT',
    headers: {
      Accept: ContentType.text.json.type,
      [Header.contentType]: ContentType.text.json.type,
    },
    body: JSON.stringify({ idToken, forceValidate }),
  });
  if (!res.ok) {
    return res.status === 503 ? 'retry' : 'fail';
  }
  // I'm seeing behavior that might be a race condition with the Set-Cookie response of this request
  // and the requests issued as part of login being complete. So, we await the body to be sure.
  await res.json();
  return 'success';
}

async function serverSignOut() {
  const res = await fetch('/api/signin', { method: 'DELETE' });
  return res.ok;
}

const reConfirmationCode = /^[0-9]*$/;

export function normalizeConfirmationCode(entry: string) {
  return entry.replace(/ /g, '');
}

export function validateConfirmationCode(entry: string): ReturnType<typeof validatePhoneNumber> {
  const compact = normalizeConfirmationCode(entry);
  const codeLength = reConfirmationCode.test(compact) ? compact.length : -1;
  return codeLength < 0 || codeLength > 6 ? 'invalid' : codeLength < 6 ? 'prefix' : 'valid';
}

export async function userSignIn(phoneNumber: string) {
  const result = await serverSignIn(phoneNumber); // , { importOnly: true });
  // if (isAbsent(result)) {
  //   return false;
  // }
  // await FireContext.signInWithToken(result.token);
  if (result.status === 'success') {
    FireContext.fire('sign_up', { method: 'sms', acct_type: 'team' });
  }
  return result.status;
}

export async function userSignOut(options?: { confirm?: boolean | undefined }) {
  // This also occurs through onIdTokenChanged.
  // await serverSignOut();
  if (options?.confirm ?? true) {
    const confirmed = await confirm(`OK to continue?`, {
      color: 'red',
      icon: 'warning',
      text: `You're about to log out.`,
    });
    if (!confirmed) {
      return;
    }
  }
  await FireContext.signOut();
}

let forceValidateToken = false;

export function refreshToken(user: User) {
  if (DEV) {
    console.log('refreshToken');
  }
  forceValidateToken = true;
  // It's not clear why, but there's some form of race condition when a force refresh of the id
  // token is called after the id token changed. A wait of 0 seconds was insufficent to resolve it,
  // which makes me a bit nervous that it could still happen. The impact is fairly benign, however.
  return wait(Seconds(1)).then(() => {
    return user.getIdToken(true);
  });
}

export async function onTokenChanged(idToken: string | undefined): Promise<OnTokenChangedResult> {
  const forceValidate = forceValidateToken;
  forceValidateToken = false;
  if (DEV) {
    console.log('onTokenChanged', {
      idToken: `${idToken?.slice(0, 8)}…${idToken?.slice(-8)}`,
      forceValidate,
    });
  }
  if (isAbsent(idToken)) {
    await serverSignOut();
    return false;
  }
  const result = await serverUpdate(idToken, forceValidate);
  if (result === 'retry') {
    return 'retry';
  }
  if (result === 'success') {
    return true;
  }
  await userSignOut({ confirm: false });
  return false;
}

class FirebaseFakeError extends CustomError {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

export async function userSignInWithPhoneNumber(
  auth: Auth,
  phoneNumber: string,
  appVerifier: ApplicationVerifier,
): Promise<ConfirmationResult> {
  if (DEV) {
    const context = await FireContext.load();
    if (!context.env) {
      const exists = await serverLookup(phoneNumber);
      if (!exists) {
        throw new FirebaseFakeError('auth/admin-restricted-operation', 'Simulated error');
      }
    }
  }
  return signInWithPhoneNumber(auth, phoneNumber, appVerifier);
}
